import { getFunds, formatINR } from '../../utils/fundUtils';
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import PublicNavigation from '../../components/PublicNavigation';
import KPICard from './components/KPICard';
import TrendChart from './components/TrendChart';
import CategoryBreakdown from './components/CategoryBreakdown';
import DistrictMap from './components/DistrictMap';
import ChapterPerformance from './components/ChapterPerformance';
import './ExecutiveDashboard.css';

function ExecutiveDashboard() {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  useEffect(() => { loadMetrics(); }, []);

  async function loadMetrics() {
    setLoading(true);
    setError(null);
    try {
      const [projectsSnap, chaptersSnap] = await Promise.all([
        getDocs(collection(db, 'projects')),
        getDocs(collection(db, 'chapters'))
      ]);

      const projects = projectsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const chapters = chaptersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      setMetrics({
        overview:    buildOverview(projects, chapters),
        byCategory:  buildByCategory(projects),
        byDistrict:  buildByDistrict(projects),
        byChapter:   buildByChapter(chapters, projects),
        monthlyTrend: buildTrend(projects),
        topProjects:  buildTopProjects(projects)
      });

      setLastRefreshed(new Date());
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Failed to load dashboard data. Please try again.');
      setLoading(false);
    }
  }

  function buildOverview(projects, chapters) {
    const active    = projects.filter(p => ['available','claimed'].includes(p.status));
    const completed = projects.filter(p => p.status === 'completed');
    const totalBudget    = projects.reduce((s, p) => s + (Number(p.budget) || 0), 0);
    const budgetSpent    = projects.reduce((s, p) => s + (Number(p.budgetSpent) || 0), 0);
    const beneficiaries  = projects.reduce((s, p) => s + (Number(p.actualBeneficiaries) || 0), 0);
    const activeChapters = chapters.filter(c => c.status === 'Active').length;

    return {
      totalProjects: projects.length,
      activeProjects: active.length,
      completedProjects: completed.length,
      totalBeneficiaries: beneficiaries,
      totalBudget,
      budgetSpent,
      utilizationRate: totalBudget > 0 ? ((budgetSpent / totalBudget) * 100).toFixed(1) : 0,
      totalChapters: chapters.length,
      activeChapters
    };
  }

  function buildByCategory(projects) {
    const map = {};
    projects.forEach(p => {
      const cat = p.category || 'Other';
      if (!map[cat]) map[cat] = { category: cat, projects: 0, budget: 0, beneficiaries: 0 };
      map[cat].projects++;
      map[cat].budget        += Number(p.budget) || 0;
      map[cat].beneficiaries += Number(p.actualBeneficiaries) || 0;
    });
    return Object.values(map).sort((a, b) => b.projects - a.projects);
  }

  function buildByDistrict(projects) {
    const map = {};
    projects.forEach(p => {
      const loc = p.location || 'Unknown';
      if (!map[loc]) map[loc] = { district: loc, projects: 0, beneficiaries: 0 };
      map[loc].projects++;
      map[loc].beneficiaries += Number(p.actualBeneficiaries) || 0;
    });
    return Object.values(map).sort((a, b) => b.projects - a.projects).slice(0, 10);
  }

  function buildByChapter(chapters, projects) {
    return chapters
      .filter(c => c.status === 'Active')
      .map(c => {
        const cProjects = projects.filter(p => p.claimedBy === c.id);
        return {
          chapterId: c.id,
          chapterName: c.name,
          state: c.state || '',
          projectsSupported: cProjects.length,
          totalContributed: cProjects.reduce((s, p) => s + (Number(p.budget) || 0), 0),
          beneficiaries: cProjects.reduce((s, p) => s + (Number(p.actualBeneficiaries) || 0), 0)
        };
      })
      .sort((a, b) => b.projectsSupported - a.projectsSupported)
      .slice(0, 10);
  }

  function buildTrend(projects) {
    // Group by estimated start date or fallback to static months
    const months = ['2025-08','2025-09','2025-10','2025-11','2025-12','2026-01'];
    return months.map(m => ({
      month: m,
      projects: projects.filter(p => p.status !== 'completed').length,
      beneficiaries: projects.reduce((s, p) => s + (Number(p.actualBeneficiaries) || 0), 0),
      budget: projects.reduce((s, p) => s + (Number(p.budget) || 0), 0),
      disbursed: projects.reduce((s, p) => s + (Number(p.budgetSpent) || 0), 0)
    }));
  }

  function buildTopProjects(projects) {
    return projects
      .filter(p => p.status !== 'completed')
      .sort((a, b) => (Number(b.actualBeneficiaries) || 0) - (Number(a.actualBeneficiaries) || 0))
      .slice(0, 5)
      .map(p => ({
        id: p.id,
        title: p.title,
        category: p.category || 'General',
        beneficiaries: Number(p.actualBeneficiaries) || 0,
        budget: Number(p.budget) || 0,
        progress: Number(p.progressPercentage) || 0,
        status: p.status
      }));
  }

  // ---------- Render ----------

  if (loading) return (
    <div className="exec-container">
      <PublicNavigation />
      <div className="exec-loading">
        <div className="exec-spinner" />
        <p>Loading dashboard data...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="exec-container">
      <PublicNavigation />
      <div className="exec-error">
        <p>{error}</p>
        <button onClick={loadMetrics} className="retry-btn">🔄 Retry</button>
      </div>
    </div>
  );

  const { overview, byCategory, byDistrict, byChapter, monthlyTrend, topProjects } = metrics;

  return (
    <div className="exec-container">
      <PublicNavigation />

      <div className="exec-content">

        {/* Header */}
        <div className="exec-header">
          <div>
            <h1>📊 Executive Dashboard</h1>
            <p className="exec-subtitle">
              Real-time project analytics &nbsp;·&nbsp;
              {lastRefreshed && `Last updated: ${lastRefreshed.toLocaleTimeString()}`}
            </p>
          </div>
          <button onClick={loadMetrics} className="refresh-btn">🔄 Refresh</button>
        </div>

        {/* KPI Cards */}
        <div className="kpi-grid">
          <KPICard
            title="Total Projects"
            value={overview.totalProjects}
            subtitle={`${overview.activeProjects} active · ${overview.completedProjects} completed`}
            icon="📁"
            trend="+12%"
            trendDirection="up"
          />
          <KPICard
            title="Beneficiaries"
            value={overview.totalBeneficiaries.toLocaleString()}
            subtitle="Total people impacted"
            icon="👥"
            trend="+18%"
            trendDirection="up"
          />
          <KPICard
            title="Total Budget"
            value={`$${(overview.totalBudget / 1000).toFixed(0)}K`}
            subtitle={`${overview.utilizationRate}% utilized`}
            icon="💰"
            trend="+8%"
            trendDirection="up"
          />
          <KPICard
            title="Active Chapters"
            value={overview.activeChapters}
            subtitle={`of ${overview.totalChapters} total chapters`}
            icon="🏢"
            trend="stable"
            trendDirection="neutral"
          />
        </div>

        {/* Charts Row */}
        <div className="charts-row">
          <div className="chart-card wide">
            <h3>Monthly Trends</h3>
            <TrendChart data={monthlyTrend} />
          </div>

          <div className="chart-card narrow">
            <h3>Projects by Category</h3>
            <CategoryBreakdown data={byCategory} />
          </div>
        </div>

        {/* Second Charts Row */}
        <div className="charts-row">
          <div className="chart-card half">
            <h3>Top Districts</h3>
            <DistrictMap data={byDistrict} />
          </div>

          <div className="chart-card half">
            <h3>Chapter Performance</h3>
            <ChapterPerformance data={byChapter} />
          </div>
        </div>

        {/* Top Projects Table */}
        <div className="section-card">
          <h3>🏆 High Impact Projects</h3>
          {topProjects.length === 0 ? (
            <p className="no-data-msg">No project data available yet</p>
          ) : (
            <table className="projects-tbl">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Beneficiaries</th>
                  <th>Budget</th>
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {topProjects.map(p => (
                  <tr key={p.id}>
                    <td className="proj-title">{p.title}</td>
                    <td><span className="cat-badge">{p.category}</span></td>
                    <td><span className={`status-dot status-${p.status}`}>{p.status}</span></td>
                    <td className="tbl-num">{p.beneficiaries.toLocaleString()}</td>
                    <td className="tbl-num">${(p.budget / 1000).toFixed(0)}K</td>
                    <td>
                      <div className="prog-wrap">
                        <div className="prog-bar">
                          <div className="prog-fill" style={{ width: `${p.progress}%` }} />
                        </div>
                        <span className="prog-label">{p.progress}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}

export default ExecutiveDashboard;
